(function () {
  'use strict';

  // ── MyBarceloAuth ──────────────────────────────────────────────────────────
  // Gestiona el flujo Headless PKCE de Barceló CIAM y las llamadas a Loyalty.
  // En modo simulado (config.simulated === true) devuelve datos fake para
  // poder trabajar antes de recibir el client_id real.
  // ──────────────────────────────────────────────────────────────────────────

  function MyBarceloAuth(config) {
    this.config  = config;
    this._prefix = 'mybarcelo_';
  }

  MyBarceloAuth.prototype = {

    login: function (email, password) {
      if (this.config.simulated) {
        return this._simulateLogin(email, password);
      }
      var self = this;
      return this._generatePKCE()
        .then(function (pkce) { return self._authorize(email, password, pkce); })
        .then(function (result) {
          return self._finishLogin(result.code, result.pkce.code_verifier);
        });
    },

    // ── Social login (Google / Facebook / Apple) ──────────────────────────────
    // Flujo OAuth+PKCE con redirección de navegador: abre un popup al authorize
    // de CIAM con el sso_provider correspondiente; el popup vuelve a la callback
    // de BEX (/mybarcelo/callback), que reenvía el `code` por postMessage. Aquí
    // validamos el `state` y completamos el intercambio de token reutilizando el
    // mismo `_finishLogin` que el login email/password.
    socialLogin: function (provider) {
      if (this.config.simulated) {
        return this._simulateLogin(provider + '@gmail.com', 'social');
      }
      var self        = this;
      var ssoProvider = this._SSO_PROVIDERS[provider];
      if (!ssoProvider) {
        return Promise.reject(new Error('Proveedor de acceso no soportado.'));
      }
      return this._generatePKCE().then(function (pkce) {
        var state = self._randomState();
        var url   = self._buildAuthorizeUrl(ssoProvider, state, pkce.code_challenge);
        return self._runPopup(url, state).then(function (code) {
          return self._finishLogin(code, pkce.code_verifier);
        });
      });
    },

    _SSO_PROVIDERS: {
      google:   'CIAMB2CGoogle',
      facebook: 'CIAMB2CFacebook',
      apple:    'CIAMB2CApple',
    },

    logout: function () {
      var self = this;
      ['access_token', 'refresh_token', 'expires_at', 'user'].forEach(function (k) {
        localStorage.removeItem(self._prefix + k);
      });
    },

    isLoggedIn: function () {
      var token     = localStorage.getItem(this._prefix + 'access_token');
      var expiresAt = parseInt(localStorage.getItem(this._prefix + 'expires_at') || '0', 10);
      return !!token && Date.now() < expiresAt;
    },

    getUser: function () {
      try { return JSON.parse(localStorage.getItem(this._prefix + 'user')); } catch (e) { return null; }
    },

    // ── Simulated flow ───────────────────────────────────────────────────────

    _simulateLogin: function (email, password) {
      var self = this;
      return this._sleep(1200).then(function () {
        if (!email || !password) {
          return Promise.reject(new Error('Por favor, completa todos los campos.'));
        }
        var name = (email.split('@')[0] || 'Usuario');
        name = name.charAt(0).toUpperCase() + name.slice(1);
        var user = {
          name:         name,
          email:        email,
          crsId:        'CRS-FAKE-001',
          loyaltyLevel: 'Silver',
          level:        1,
          levelDesc:    'Essential',
          discount:     5,
        };
        self._saveTokens({
          access_token:  'fake_at_' + Date.now(),
          refresh_token: 'fake_rt_' + Date.now(),
        });
        self._saveUser(user);
        return user;
      });
    },

    // ── Real PKCE flow (unused until client_id arrives) ──────────────────────

    _generatePKCE: function () {
      var array = new Uint8Array(32);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(array);
      } else {
        for (var i = 0; i < 32; i++) {
          array[i] = Math.floor(Math.random() * 256);
        }
      }
      var verifier = btoa(String.fromCharCode.apply(null, array))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

      if (window.crypto && window.crypto.subtle && window.crypto.subtle.digest) {
        return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
          .then(function (digest) {
            var challenge = btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
              .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
            return { code_verifier: verifier, code_challenge: challenge };
          });
      } else {
        var hex = this._sha256(verifier);
        var bytes = new Uint8Array(hex.match(/.{1,2}/g).map(function (byte) {
          return parseInt(byte, 16);
        }));
        var challenge = btoa(String.fromCharCode.apply(null, bytes))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        return Promise.resolve({ code_verifier: verifier, code_challenge: challenge });
      }
    },

    _sha256: function (ascii) {
      function rightRotate(value, amount) {
        return (value >>> amount) | (value << (32 - amount));
      }
      var mathPow = Math.pow;
      var maxWord = mathPow(2, 32);
      var lengthProperty = 'length';
      var i, j;
      var result = '';
      var words = [];
      var asciiBitLength = ascii[lengthProperty] * 8;
      var hash = [];
      var k = [];
      var primeCounter = 0;
      var isComposite = {};
      for (var candidate = 2; primeCounter < 64; candidate++) {
        if (!isComposite[candidate]) {
          for (i = 0; i < 313; i += candidate) {
            isComposite[i] = candidate;
          }
          hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
          k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
        }
      }
      ascii += '\x80';
      while (ascii[lengthProperty] % 64 - 56) ascii += '\x00';
      for (i = 0; i < ascii[lengthProperty]; i++) {
        j = ascii.charCodeAt(i);
        if (j >> 8) return;
        words[i >> 2] |= j << ((3 - i) % 4) * 8;
      }
      words[words[lengthProperty]] = ((asciiBitLength / maxWord) | 0);
      words[words[lengthProperty]] = (asciiBitLength);
      for (j = 0; j < words[lengthProperty];) {
        var w = words.slice(j, j += 16);
        var oldHash = hash;
        hash = hash.slice(0, 8);
        for (i = 0; i < 64; i++) {
          var w15 = w[i - 15], w2 = w[i - 2];
          var a = hash[0], e = hash[4];
          var temp1 = hash[7]
            + (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25))
            + ((e & hash[5]) ^ ((~e) & hash[6]))
            + k[i]
            + (w[i] = (i < 16) ? w[i] : (
                w[i - 16]
                + (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3))
                + w[i - 7]
                + (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))
              ) | 0
            );
          var temp2 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22))
            + ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
          hash = [(temp1 + temp2) | 0].concat(hash);
          hash[4] = (hash[4] + temp1) | 0;
        }
        for (i = 0; i < 8; i++) {
          hash[i] = (hash[i] + oldHash[i]) | 0;
        }
      }
      for (i = 0; i < 8; i++) {
        for (j = 3; j + 1; j--) {
          var b = (hash[i] >> (j * 8)) & 255;
          result += ((b < 16) ? '0' : '') + b.toString(16);
        }
      }
      return result;
    },

    _authorize: function (email, password, pkce) {
      var cfg    = this.config;
      var params = new URLSearchParams({
        email:                 email,
        password:              password,
        response_type:         'code',
        client_id:             cfg.clientId,
        redirect_uri:          cfg.redirectUri,
        code_challenge:        pkce.code_challenge,
        code_challenge_method: 'S256',
        scope:                 'refresh_token openid api',
      });
      return fetch(cfg.restBase + '/mybarcelo/authorize', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      }).then(function (resp) {
        if (!resp.ok) return Promise.reject(new Error('Credenciales incorrectas. Inténtalo de nuevo.'));
        return resp.json().then(function (data) {
          if (data.code === 'mybarcelo_loyalty_error') {
            return Promise.reject(new Error('Credenciales incorrectas o error en el servidor.'));
          }
          return { pkce: pkce, code: data.code };
        });
      });
    },

    _exchangeToken: function (code, verifier) {
      var cfg    = this.config;
      var params = new URLSearchParams({
        grant_type:    'authorization_code',
        code:          code,
        code_verifier: verifier,
        client_id:     cfg.clientId,
        redirect_uri:  cfg.redirectUri,
      });
      return fetch(cfg.restBase + '/mybarcelo/token', {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    params.toString(),
      }).then(function (resp) {
        if (!resp.ok) return Promise.reject(new Error('Error al obtener el token de acceso.'));
        return resp.json();
      });
    },

    // Cola común a login email/password y social: token → userinfo → loyalty.
    _finishLogin: function (code, verifier) {
      var self = this;
      return this._exchangeToken(code, verifier).then(function (tokens) {
        self._saveTokens(tokens);
        return self._getUserInfo(tokens.access_token).then(function (userInfo) {
          return self._fetchLoyalty(tokens.access_token, userInfo.crsId).then(function (loyalty) {
            var user = Object.assign({}, userInfo, loyalty);
            self._saveUser(user);
            return user;
          });
        });
      });
    },

    _buildAuthorizeUrl: function (ssoProvider, state, codeChallenge) {
      var cfg    = this.config;
      var params = new URLSearchParams({
        client_id:             cfg.clientId,
        redirect_uri:          cfg.redirectUri,
        response_type:         'code',
        sso_provider:          ssoProvider,
        state:                 state,
        code_challenge:        codeChallenge,
        code_challenge_method: 'S256',
      });
      return cfg.ciamBase + '/services/oauth2/authorize?' + params.toString();
    },

    _randomState: function () {
      var array = new Uint8Array(16);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(array);
      } else {
        for (var i = 0; i < 16; i++) { array[i] = Math.floor(Math.random() * 256); }
      }
      return Array.prototype.map.call(array, function (b) {
        return ('0' + b.toString(16)).slice(-2);
      }).join('');
    },

    // Abre el popup del authorize y resuelve con el `code` que la callback de BEX
    // reenvía por postMessage. Valida origen y `state` (anti-CSRF).
    _runPopup: function (url, expectedState) {
      return new Promise(function (resolve, reject) {
        var popup = window.open(
          url,
          'mybarcelo_social',
          'width=520,height=640,menubar=no,toolbar=no,location=no,status=no'
        );
        if (!popup) {
          reject(new Error('No se pudo abrir la ventana de acceso. Revisa el bloqueador de pop-ups.'));
          return;
        }

        var settled = false;

        function cleanup() {
          settled = true;
          window.removeEventListener('message', onMessage);
          clearInterval(timer);
          try { if (popup && !popup.closed) popup.close(); } catch (e) {}
        }

        function onMessage(event) {
          if (event.origin !== window.location.origin) return;
          var data = event.data || {};
          if (data.type !== 'mybarcelo-social') return;
          cleanup();
          if (!data.code || data.state !== expectedState) {
            reject(new Error('La validación de seguridad ha fallado. Inténtalo de nuevo.'));
            return;
          }
          resolve(data.code);
        }

        window.addEventListener('message', onMessage);

        var timer = setInterval(function () {
          if (popup.closed && !settled) {
            cleanup();
            reject(new Error('Has cerrado la ventana antes de completar el acceso.'));
          }
        }, 500);
      });
    },

    _getUserInfo: function (accessToken) {
      return fetch(this.config.restBase + '/mybarcelo/userprofile?access_token=' + encodeURIComponent(accessToken), {
        headers: { 'Content-Type': 'application/json' },
      }).then(function (resp) {
        if (!resp.ok) return Promise.reject(new Error('Error al obtener datos del usuario.'));
        return resp.json();
      });
    },

    _fetchLoyalty: function (accessToken, crsId) {
      var base = this.config.restBase;
      return fetch(base + '/mybarcelo/userids?access_token=' + encodeURIComponent(accessToken))
        .then(function (resp) { return resp.ok ? resp.json() : {}; })
        .then(function (data) {
          if (!data.crm_id) return {};
          return fetch(base + '/mybarcelo/userinfo?access_token=' + encodeURIComponent(accessToken) + '&crm_id=' + encodeURIComponent(data.crm_id))
            .then(function (resp) { return resp.ok ? resp.json() : {}; });
        })
        .catch(function () { return {}; });
    },

    // ── Storage ──────────────────────────────────────────────────────────────

    _saveTokens: function (tokens) {
      localStorage.setItem(this._prefix + 'access_token',  tokens.access_token  || '');
      localStorage.setItem(this._prefix + 'refresh_token', tokens.refresh_token || '');
      localStorage.setItem(this._prefix + 'expires_at',    String(Date.now() + 3600000));
    },

    _saveUser: function (user) {
      localStorage.setItem(this._prefix + 'user', JSON.stringify(user));
    },

    _sleep: function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); },
  };

  // ── MyBarceloModal ─────────────────────────────────────────────────────────
  // Controla el modal de login y el estado del indicador en el header.
  // ──────────────────────────────────────────────────────────────────────────

  function MyBarceloModal(auth) {
    this.auth      = auth;
    this.modal     = document.getElementById('mybarcelo-modal');
    this.indicator = document.getElementById('mybarcelo-login');
  }

  MyBarceloModal.prototype = {

    init: function () {
      var self = this;

      if (this.auth.isLoggedIn()) {
        var user = this.auth.getUser();
        if (user) {
          this._setLoggedState(user);
          this._refreshLoyaltyIfMissing(user);
        } else {
          this._setLoggedOutState();
        }
      } else {
        this._setLoggedOutState();
      }

      // Cards nuevas del listado (filtros, «cargar más»): se pintan como
      // anónimas en el servidor y hay que volver a aplicarles el nivel.
      document.addEventListener('bex:products-updated', function () {
        if (!self.auth.isLoggedIn()) return;
        var current = self.auth.getUser();
        if (current) self._updateProductCards(current);
      });

      if (!this.modal || !this.indicator) return;

      this.indicator.addEventListener('click', function (e) { self._onIndicatorClick(e); });

      var logoutBtn = this.indicator.querySelector('.mybarcelo-login__logout');
      if (logoutBtn) logoutBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        self.auth.logout();
        self._setLoggedOutState();
      });

      // Cerrar el desplegable al clicar fuera del indicador.
      document.addEventListener('click', function (e) {
        if (self.indicator && !self.indicator.contains(e.target)) self._closeMenu();
      });

      // Disparadores de login: el hint de la product-card y el botón del banner loyalty.
      // Captura para adelantarse a la navegación del <a> de la card y a su onclick (ga4).
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest && e.target.closest('.product-card__price-hint, .js-myb-login-trigger');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        self.open();
      }, true);

      var backdrop = this.modal.querySelector('.mybarcelo-modal__backdrop');
      var closeBtn = this.modal.querySelector('.mybarcelo-modal__close');
      if (backdrop) backdrop.addEventListener('click', function () { self.close(); });
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

      var form = document.getElementById('mybarcelo-login-form');
      if (form) form.addEventListener('submit', function (e) { self._handleSubmit(e); });

      var eyeBtn = document.getElementById('mybarcelo-toggle-password');
      if (eyeBtn) eyeBtn.addEventListener('click', function () { self._togglePassword(); });

      var tabs = this.modal.querySelectorAll('.mybarcelo-modal__tab');
      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () { self._switchTab(btn.dataset.tab); });
      });

      var socialBtns = this.modal.querySelectorAll('.mybarcelo-modal__social-btn[data-provider]');
      socialBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { self._handleSocial(btn.dataset.provider); });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal.classList.contains('is-open')) self.close();
      });
    },

    open: function () {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var emailInput = document.getElementById('mybarcelo-email');
      if (emailInput) setTimeout(function () { emailInput.focus(); }, 350);
    },

    close: function () {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    _onIndicatorClick: function (e) {
      // Clicks dentro del menú los gestiona el propio botón.
      if (e && e.target.closest && e.target.closest('.mybarcelo-login__menu')) return;
      if (this.auth.isLoggedIn()) {
        this._toggleMenu();
        return;
      }
      this.open();
    },

    _switchTab: function (tab) {
      var tabs     = this.modal.querySelectorAll('.mybarcelo-modal__tab');
      var sections = this.modal.querySelectorAll('.mybarcelo-modal__section');
      tabs.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.tab === tab);
      });
      sections.forEach(function (section) {
        section.classList.toggle('d-none', section.dataset.section !== tab);
      });
    },

    _handleSubmit: function (e) {
      e.preventDefault();
      var self     = this;
      var email    = (document.getElementById('mybarcelo-email')?.value || '').trim();
      var password = document.getElementById('mybarcelo-password')?.value || '';
      var btn      = document.getElementById('mybarcelo-submit');
      var errorEl  = document.getElementById('mybarcelo-error');
      var loading  = this.modal.querySelector('.mybarcelo-modal__loading');
      var origHTML = btn ? btn.innerHTML : '';

      if (btn)     { btn.disabled = true; }
      if (loading) loading.classList.remove('d-none');
      if (errorEl) errorEl.classList.add('d-none');

      this.auth.login(email, password)
        .then(function (user) {
          self._setLoggedState(user);
          self.close();
        })
        .catch(function (err) {
          if (errorEl) {
            errorEl.textContent = (err && err.message) ? err.message : 'Error al iniciar sesión. Inténtalo de nuevo.';
            errorEl.classList.remove('d-none');
          }
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.innerHTML = origHTML; }
          if (loading) loading.classList.add('d-none');
        });
    },

    _handleSocial: function (provider) {
      var self    = this;
      var errorEl = document.getElementById('mybarcelo-error');
      var btn     = this.modal.querySelector('.mybarcelo-modal__social-btn[data-provider="' + provider + '"]');

      // Los tres proveedores de CIAM (Google/Facebook/Apple) comparten el mismo
      // flujo headless: solo cambia el sso_provider (docs/MyBarcelo/HEADLESS.pdf).
      // Activo cualquier proveedor presente en el mapa _SSO_PROVIDERS.
      if (!this.auth._SSO_PROVIDERS[provider]) {
        if (errorEl) {
          errorEl.textContent = 'Este método de acceso estará disponible próximamente.';
          errorEl.classList.remove('d-none');
        }
        return;
      }

      if (errorEl) errorEl.classList.add('d-none');
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      this.auth.socialLogin(provider)
        .then(function (user) {
          self._setLoggedState(user);
          self.close();
        })
        .catch(function (err) {
          if (errorEl) {
            errorEl.textContent = (err && err.message) ? err.message : 'No se pudo completar el acceso.';
            errorEl.classList.remove('d-none');
          }
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        });
    },

    // Sesiones guardadas antes de la integración loyalty (o cuyo fetch de nivel
    // falló durante el login): el user de localStorage no trae levelDesc y el
    // badge se quedaría en 'general' para siempre. Reintenta la carga del nivel
    // con el token vigente y, si llega, actualiza storage y todo el estado
    // (badge del header, tags del detalle, fees y widget).
    _refreshLoyaltyIfMissing: function (user) {
      var self = this;
      if (user.levelDesc) return;
      var token = localStorage.getItem(this.auth._prefix + 'access_token');
      if (!token) return;
      this.auth._fetchLoyalty(token, user.crsId).then(function (loyalty) {
        if (loyalty && loyalty.levelDesc) {
          var updated = Object.assign({}, user, loyalty);
          self.auth._saveUser(updated);
          self._setLoggedState(updated);
        }
      });
    },

    _setLoggedState: function (user) {
      if (this.indicator) {
        this.indicator.dataset.mybarceloState = 'logged';
        var notLoggedEl = this.indicator.querySelector('.mybarcelo-login__not-logged');
        var loggedEl    = this.indicator.querySelector('.mybarcelo-login__logged');
        var nameEl      = this.indicator.querySelector('.mybarcelo-login__name');
        var badgeEl     = this.indicator.querySelector('.mybarcelo-login__badge');
        if (notLoggedEl) notLoggedEl.classList.add('d-none');
        if (loggedEl) {
          loggedEl.classList.remove('d-none');
          loggedEl.classList.add('d-flex');
        }
        if (nameEl) nameEl.textContent = user.name || (user.email || '').split('@')[0] || 'Hola';

        // Refleja el nivel de loyalty: color del badge + nombre del nivel.
        var level = user.levelDesc ? String(user.levelDesc).toLowerCase() : '';
        var validLevels = ['essential', 'special', 'unique', 'infinity'];
        var hasLevel = validLevels.indexOf(level) !== -1;
        if (badgeEl) {
          validLevels.forEach(function (l) { badgeEl.classList.remove('mybarcelo-login__badge--' + l); });
          if (hasLevel) badgeEl.classList.add('mybarcelo-login__badge--' + level);
        }
        this._toggleLoyaltyTags(hasLevel ? level : 'general');
      }

      this._updateProductCards(user);
      this._updateDetailFees(user);
      this._toggleLoyaltyBanner(false);
      this._updateWidgetData(user);
      this._syncForgotLink();
    },

    // Tras el login, pide al backend el `data` cifrado del widget Kora con
    // los datos del usuario (el token de cifrado nunca sale al navegador) y
    // reescribe el src del iframe ya renderizado en el detalle de producto.
    _updateWidgetData: function (user) {
      var wrapper = document.querySelector('.widget-wrapper[data-product-id]');
      if (!wrapper) return;
      var iframe = wrapper.querySelector('#widget');
      if (!iframe) return;

      // Guardar el src anónimo original (render del servidor) antes de la
      // primera sustitución, para poder restaurarlo al hacer logout.
      if (!iframe.dataset.mybOriginalSrc) {
        iframe.dataset.mybOriginalSrc = iframe.src;
      }

      var phone      = user.phone_number || '';
      var phoneMatch = phone.match(/^(\+\d{1,3})(.*)$/);

      // Al widget se le pasa SIEMPRE el `level` numérico del getinfouser
      // (1=Essential, 2=Special, 3=Unique, 4=Infinity...), lo conozcamos o no
      // en nuestra UI: Kora es quien resuelve qué significa cada tier.
      var loyaltyTierId = user.level ? String(user.level) : '';

      var params = new URLSearchParams({
        product_id:    wrapper.dataset.productId,
        template:      wrapper.dataset.widgetEh === '1' ? 'experiencias-hoteles' : '',
        isUnique:      wrapper.dataset.widgetUnique === '1' ? '1' : '0',
        isGeneric:     wrapper.dataset.widgetGeneric === '1' ? '1' : '0',
        isEclipse:     wrapper.dataset.widgetEclipse === '1' ? '1' : '0',
        firstName:     user.given_name || user.name || '',
        lastName:      user.family_name || user.surname || '',
        email:         user.email || '',
        phonePrefix:   phoneMatch ? phoneMatch[1] : '',
        phoneNumber:   phoneMatch ? phoneMatch[2] : '',
        loyaltyTierId: loyaltyTierId,
        // identifier del getinfouser (p. ej. "ES17000089821"): el servidor lo
        // pone en loyalty_customer_id y external_customer_id del payload.
        identifier:    user.identifier || '',
      });

      fetch(this.auth.config.restBase + '/mybarcelo/widget-data', {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    params.toString(),
      }).then(function (resp) {
        return resp.ok ? resp.json() : null;
      }).then(function (json) {
        // Fuera de prod el servidor incluye el payload en claro para depurar.
        if (json && json.payload) console.log('[myBarcelo] Kora widget payload:', json.payload);
        if (json && json.src) iframe.src = json.src;
      }).catch(function () {
        // Deja el iframe con los datos anónimos: no bloquea la reserva.
      });
    },

    // Alterna los badges loyalty del detalle de producto (galería + precio)
    // al nivel indicado. Ambos comparten la clase myb-detail-tag, así que una
    // sola función los sincroniza sin duplicar lógica por ubicación.
    _toggleLoyaltyTags: function (level) {
      document.querySelectorAll('.myb-detail-tag[data-myb-tag]').forEach(function (tag) {
        tag.classList.toggle('d-none', tag.dataset.mybTag !== level);
      });
    },

    _setLoggedOutState: function () {
      if (this.indicator) {
        this.indicator.dataset.mybarceloState = 'not-logged';
        var notLoggedEl = this.indicator.querySelector('.mybarcelo-login__not-logged');
        var loggedEl    = this.indicator.querySelector('.mybarcelo-login__logged');
        if (notLoggedEl) notLoggedEl.classList.remove('d-none');
        if (loggedEl) {
          loggedEl.classList.add('d-none');
          loggedEl.classList.remove('d-flex');
        }
      }

      this._closeMenu();
      this._resetProductCards();
      this._resetDetailFees();
      this._resetWidgetData();
      this._toggleLoyaltyBanner(true);
      this._toggleLoyaltyTags('general');
      this._syncForgotLink();
    },

    // El enlace «¿Has olvidado tu contraseña?» depende de la sesión. Sin esto,
    // tras registrarse y cerrar sesión seguía oculto hasta recargar la página.
    _syncForgotLink: function () {
      if (window.BexForgotPassword && window.BexForgotPassword.ajustarVisibilidadEnlace) {
        window.BexForgotPassword.ajustarVisibilidadEnlace();
      }
    },

    // Restaura el widget Kora al estado anónimo (el src con el que lo renderizó
    // el servidor) al cerrar sesión; si no se llegó a personalizar, no hace nada.
    _resetWidgetData: function () {
      var iframe = document.querySelector('.widget-wrapper[data-product-id] #widget');
      if (iframe && iframe.dataset.mybOriginalSrc && iframe.src !== iframe.dataset.mybOriginalSrc) {
        iframe.src = iframe.dataset.mybOriginalSrc;
      }
    },

    // Desplegable del indicador logueado (temporal, pendiente de diseño).
    _toggleMenu: function () {
      if (!this.indicator) return;
      var menu = this.indicator.querySelector('.mybarcelo-login__menu');
      if (menu) menu.classList.toggle('d-none');
    },

    _closeMenu: function () {
      if (!this.indicator) return;
      var menu = this.indicator.querySelector('.mybarcelo-login__menu');
      if (menu) menu.classList.add('d-none');
    },

    // Muestra/oculta el banner loyalty (CTA login). Oculto cuando el usuario está logueado.
    // Oculta/muestra los CTAs de login (banner loyalty y CTA sobre el widget
    // del detalle): visibles solo sin sesión.
    _toggleLoyaltyBanner: function (show) {
      document.querySelectorAll('.mybarcelo-banner, .mybarcelo-widget-cta').forEach(function (banner) {
        banner.classList.toggle('d-none', !show);
      });
    },

    // Transforma las product-cards al estado logueado: tacha el precio base,
    // muestra el precio con descuento, activa el badge de nivel y oculta el caption.
    // Caché del fetch de precios (por tier+catálogos+fechas) para no repetir la
    // llamada cuando el estado logueado se re-aplica (init + refresh del nivel).
    _koraPricesCache: null,

    // Fechas activas del filtro del listado (daterangepicker de #date). Si el
    // usuario no ha filtrado, se envía vacío y no se filtra por fechas en Kora.
    _activeDateFilter: function () {
      var el = document.getElementById('date');
      if (!el || !el.value || !window.jQuery) return { from: '', to: '' };
      var drp = window.jQuery(el).data('daterangepicker');
      if (!drp || !drp.startDate || !drp.endDate) return { from: '', to: '' };
      return {
        from: drp.startDate.format('YYYY-MM-DD'),
        to:   drp.endDate.format('YYYY-MM-DD'),
      };
    },

    // Precios reales de Kora para las cards de la vista con el nivel del
    // usuario (bex/v1/mybarcelo/prices). El descuento NO se calcula en cliente:
    // Kora devuelve amount_with_discount por producto (y el precio base puede
    // variar según la fecha). En vistas con catálogos en la config (listados,
    // EH) se pide por catálogo; si no los hay (property, planes, itinerarios),
    // se piden los data-myb-product-id presentes en el DOM. Resuelve null si
    // no aplica o falla.
    _fetchKoraPrices: function (user) {
      var cfg         = this.auth.config;
      var level       = user && user.levelDesc ? String(user.levelDesc).toLowerCase() : '';
      var validLevels = ['essential', 'special', 'unique', 'infinity'];
      var tier        = (validLevels.indexOf(level) !== -1 && user.level) ? String(user.level) : '';
      var catalogues  = (cfg.catalogues || []).join(',');

      var products = '';
      if (!catalogues) {
        var ids = [];
        document.querySelectorAll('[data-myb-product-id]').forEach(function (el) {
          var id = el.getAttribute('data-myb-product-id');
          if (id && ids.indexOf(id) === -1) ids.push(id);
        });
        products = ids.slice(0, 20).join(',');
      }
      if (!tier || (!catalogues && !products)) return Promise.resolve(null);

      var dates = this._activeDateFilter();
      var key   = tier + '|' + catalogues + '|' + products + '|' + dates.from + '|' + dates.to;
      if (this._koraPricesCache && this._koraPricesCache.key === key) {
        return Promise.resolve(this._koraPricesCache.prices);
      }

      var self   = this;
      var params = new URLSearchParams({
        catalogues:      catalogues,
        products:        products,
        loyalty_tier_id: tier,
        from:            dates.from,
        to:              dates.to,
      });
      return fetch(cfg.restBase + '/mybarcelo/prices?' + params.toString())
        .then(function (resp) { return resp.ok ? resp.json() : null; })
        .then(function (json) {
          var prices = json && json.prices ? json.prices : null;
          if (prices) self._koraPricesCache = { key: key, prices: prices };
          return prices;
        })
        .catch(function () { return null; });
    },

    _updateProductCards: function (user) {
      var self = this;
      this._fetchKoraPrices(user).then(function (prices) {
        self._applyProductCards(user, prices || {});
      });
    },

    _applyProductCards: function (user, prices) {
      var level = user && user.levelDesc ? String(user.levelDesc).toLowerCase() : '';
      var validLevels = ['essential', 'special', 'unique', 'infinity'];

      // Con sesión iniciada, el CTA "inicia sesión" de las cards no aplica,
      // tenga o no descuento la card.
      if (validLevels.indexOf(level) !== -1) {
        document.querySelectorAll('.product-card__price-hint').forEach(function (hint) {
          hint.classList.add('d-none');
        });
      }

      // Precio de una card: SOLO el real de Kora (amount + amount_with_discount
      // del endpoint). Kora es la fuente de la verdad: si el producto no vino en
      // la respuesta (sin precios para la fecha => configuración del producto en
      // Kora), no se muestra descuento. NUNCA se calcula el precio en cliente.
      function resolvePrice(el) {
        if (validLevels.indexOf(level) === -1) return null;
        var pid  = el.getAttribute('data-myb-product-id') || '';
        var kora = pid && prices[pid] ? prices[pid] : null;
        if (kora && kora.amount && kora.amount_with_discount && parseFloat(kora.amount_with_discount) < parseFloat(kora.amount)) {
          var base = parseFloat(kora.amount);
          var disc = parseFloat(kora.amount_with_discount);
          return {
            base:     base,
            price:    disc,
            currency: kora.currency || el.getAttribute('data-myb-currency') || '',
            pct:      Math.round((1 - disc / base) * 100),
            // El descuento puede venir de promos GENERALES (aplican a todos):
            // el precio se muestra siempre, pero el badge de nivel solo si
            // Kora dice que hay promo loyalty para el tier del usuario.
            hasLoyalty: kora.has_loyalty !== false,
          };
        }
        return null;
      }

      // Logueado con nivel y sin precio de Kora para su tier => la promo del
      // producto no aplica a su nivel: no se muestra NINGÚN badge (tampoco el
      // genérico del render anónimo, que solo mira has_loyalty_promotions).
      var hasLevel = validLevels.indexOf(level) !== -1;

      document.querySelectorAll('.product-card__price-block').forEach(function (block) {
        var p = resolvePrice(block);
        if (!p) {
          if (hasLevel) {
            block.querySelectorAll('.product-card__tag').forEach(function (tag) {
              tag.classList.add('d-none');
            });
          }
          return;
        }

        var priceEl = block.querySelector('.product-card__price');
        var oldEl   = block.querySelector('.product-card__price-old');
        if (priceEl) priceEl.textContent = p.price + ' ' + p.currency;
        if (oldEl) {
          oldEl.textContent = p.base + ' ' + p.currency;
          oldEl.classList.remove('d-none');
        }

        block.querySelectorAll('.product-card__tag').forEach(function (tag) {
          var isActive = p.hasLoyalty && tag.getAttribute('data-myb-tag') === level;
          tag.classList.toggle('d-none', !isActive);
          if (isActive) {
            var discEl = tag.querySelector('.myb-tag__discount');
            if (discEl) discEl.textContent = '-' + p.pct + '%';
          }
        });

        var hint = block.querySelector('.product-card__price-hint');
        if (hint) hint.classList.add('d-none');

        block.setAttribute('data-mybarcelo-state', 'logged');
      });

      // Cards simples (experiencias-hoteles / unique): misma resolución sobre .card-price.
      function fmtMoney(n) { return (Math.round(n * 100) / 100).toFixed(2).replace('.', ','); }
      document.querySelectorAll('.experiencia-card[data-myb-base-price]').forEach(function (card) {
        var p = resolvePrice(card);
        if (!p) {
          if (hasLevel) {
            card.querySelectorAll('.myb-card-badge').forEach(function (tag) {
              tag.classList.add('d-none');
            });
          }
          return;
        }

        var priceEl = card.querySelector('.card-price');
        if (priceEl) {
          priceEl.innerHTML = fmtMoney(p.price) + ' ' + p.currency
            + ' <span>' + fmtMoney(p.base) + ' ' + p.currency + '</span>';
        }

        card.querySelectorAll('.myb-card-badge').forEach(function (tag) {
          var isActive = p.hasLoyalty && tag.getAttribute('data-myb-tag') === level;
          tag.classList.toggle('d-none', !isActive);
          if (isActive) {
            var discEl = tag.querySelector('.myb-tag__discount');
            if (discEl) discEl.textContent = '-' + p.pct + '%';
          }
        });
      });
    },

    // Revierte las product-cards al estado no-logueado. El estado anónimo puede
    // incluir descuento de Kora (promos generales, data-myb-anon-discount):
    // en ese caso se restaura el descontado con el base tachado.
    _resetProductCards: function () {
      document.querySelectorAll('.product-card__price-block').forEach(function (block) {
        var base = block.getAttribute('data-myb-base-price');
        var currency = block.getAttribute('data-myb-currency') || '';
        var anonDiscount = parseFloat(block.getAttribute('data-myb-anon-discount'));

        var priceEl = block.querySelector('.product-card__price');
        var oldEl   = block.querySelector('.product-card__price-old');
        if (priceEl) priceEl.textContent = (isNaN(anonDiscount) ? base : anonDiscount) + ' ' + currency;
        if (oldEl) {
          oldEl.textContent = base + ' ' + currency;
          oldEl.classList.toggle('d-none', isNaN(anonDiscount));
        }

        block.querySelectorAll('.product-card__tag').forEach(function (tag) {
          tag.classList.toggle('d-none', tag.getAttribute('data-myb-tag') !== 'general');
        });

        var hint = block.querySelector('.product-card__price-hint');
        if (hint) hint.classList.remove('d-none');

        block.setAttribute('data-mybarcelo-state', 'not-logged');
      });

      // Cards simples (experiencias-hoteles / unique): vuelta al estado anónimo
      // (con descuento general de Kora si lo hay, base tachado).
      function fmtMoney(n) { return (Math.round(parseFloat(n) * 100) / 100).toFixed(2).replace('.', ','); }
      document.querySelectorAll('.experiencia-card[data-myb-has-promo="1"]').forEach(function (card) {
        var base = card.getAttribute('data-myb-base-price');
        var currency = card.getAttribute('data-myb-currency') || '';
        var anonDiscount = parseFloat(card.getAttribute('data-myb-anon-discount'));

        var priceEl = card.querySelector('.card-price');
        if (priceEl && !isNaN(parseFloat(base))) {
          priceEl.innerHTML = isNaN(anonDiscount)
            ? fmtMoney(base) + ' ' + currency
            : fmtMoney(anonDiscount) + ' ' + currency + ' <span>' + fmtMoney(base) + ' ' + currency + '</span>';
        }

        card.querySelectorAll('.myb-card-badge').forEach(function (tag) {
          tag.classList.toggle('d-none', tag.getAttribute('data-myb-tag') !== 'general');
        });
      });
    },

    // Aplica el descuento loyalty a las tarifas del acordeón "Price" del
    // detalle genérico: tacha el precio base y muestra el descontado en teal.
    // El importe descontado es SIEMPRE el amount_with_discount de Kora (compone
    // varias promos; el % del tier NO sirve para recalcularlo): se piden las
    // tarifas con el tier del usuario a bex/v1/mybarcelo/product-fees y, si la
    // llamada falla, se usa el data-myb-fee-discount del render anónimo. Sin
    // dato de Kora, la tarifa se queda con el precio base.
    // El badge de nivel junto al título lo alterna _toggleLoyaltyTags.
    _updateDetailFees: function (user) {
      var level = user && user.levelDesc ? String(user.levelDesc).toLowerCase() : '';
      var validLevels = ['essential', 'special', 'unique', 'infinity'];
      if (validLevels.indexOf(level) === -1) return;

      var fees       = document.querySelectorAll('.myb-fee[data-myb-has-promo="1"]');
      var detailTags = document.querySelectorAll('.myb-detail-tag');
      if (!fees.length && !detailTags.length) return;

      // Mismo formato que el servidor: number_format(x, 2, ',', '.')
      function fmt(n) {
        var parts = (Math.round(n * 100) / 100).toFixed(2).split('.');
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
      }

      function apply(json) {
        var tierFees = json && json.fees ? json.fees : null;

        // Si Kora dice que el producto NO tiene promoción para el tier del
        // usuario, no debe verse ningún badge en el detalle (tampoco el
        // genérico): _toggleLoyaltyTags ya mostró el del nivel, se revierte.
        if (json && !json.has_loyalty) {
          detailTags.forEach(function (tag) { tag.classList.add('d-none'); });
        }

        // Banner sticky del detalle (.widget-banner__price): precio del tier
        // de Kora (base tachado + descontado). Sin descuento para su tier, se
        // deja el render anónimo (que ya trae el descuento general si lo hay).
        var sticky = document.querySelector('.widget-banner__price');
        if (sticky && json && json.product) {
          var stAmt  = parseFloat(json.product.amount);
          var stDisc = parseFloat(json.product.amount_with_discount);
          if (!isNaN(stAmt) && !isNaN(stDisc) && stDisc < stAmt) {
            if (!sticky.dataset.mybOriginalHtml) sticky.dataset.mybOriginalHtml = sticky.innerHTML;
            var stCur  = json.product.currency || 'EUR';
            var stFrom = sticky.querySelector('span'); // el "desde" localizado del servidor
            sticky.innerHTML = '<span>' + (stFrom ? stFrom.textContent : '') + '</span> '
              + '<span class="text-decoration-line-through">' + fmt(stAmt) + ' ' + stCur + '</span> '
              + fmt(stDisc) + ' ' + stCur;
          } else if (sticky.dataset.mybOriginalHtml) {
            sticky.innerHTML = sticky.dataset.mybOriginalHtml;
          }
        }

        fees.forEach(function (fee) {
          var key  = fee.getAttribute('data-myb-fee-key') || '';
          var kora = tierFees && key && tierFees[key] ? tierFees[key] : null;

          var base       = kora ? parseFloat(kora.amount) : parseFloat(fee.getAttribute('data-myb-fee-base'));
          var discounted = kora ? parseFloat(kora.amount_with_discount) : parseFloat(fee.getAttribute('data-myb-fee-discount'));
          if (isNaN(base) || isNaN(discounted) || discounted >= base) return;
          var currency = (kora && kora.currency) || fee.getAttribute('data-myb-currency') || 'EUR';

          var priceEl = fee.querySelector('.myb-fee__price');
          var oldEl   = fee.querySelector('.myb-fee__old');
          if (priceEl) {
            priceEl.textContent = fmt(discounted) + ' ' + currency;
            priceEl.classList.add('myb-fee__price--discounted');
          }
          if (oldEl) {
            oldEl.textContent = fmt(base) + ' ' + currency;
            oldEl.classList.remove('d-none');
          }
        });
      }

      var wrapper   = document.querySelector('.widget-wrapper[data-product-id]');
      var productId = wrapper ? wrapper.getAttribute('data-product-id') : '';
      var tier      = user.level ? String(user.level) : '';
      if (!productId || !tier) { apply(null); return; }

      var params = new URLSearchParams({ product_id: productId, loyalty_tier_id: tier });
      fetch(this.auth.config.restBase + '/mybarcelo/product-fees?' + params.toString())
        .then(function (resp) { return resp.ok ? resp.json() : null; })
        .then(function (json) { apply(json); })
        .catch(function () { apply(null); });
    },

    // Revierte las tarifas del detalle al estado no-logueado. El estado anónimo
    // puede incluir descuento general de Kora (data-myb-fee-discount): en ese
    // caso se restaura el descontado con el base tachado. El base formateado se
    // recupera del propio span tachado (siempre conserva el base).
    _resetDetailFees: function () {
      function fmt(n) {
        var parts = (Math.round(n * 100) / 100).toFixed(2).split('.');
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
      }
      document.querySelectorAll('.myb-fee').forEach(function (fee) {
        var priceEl      = fee.querySelector('.myb-fee__price');
        var oldEl        = fee.querySelector('.myb-fee__old');
        var anonDiscount = parseFloat(fee.getAttribute('data-myb-fee-discount'));
        var currency     = fee.getAttribute('data-myb-currency') || 'EUR';

        if (priceEl && oldEl) {
          priceEl.textContent = isNaN(anonDiscount)
            ? oldEl.textContent
            : fmt(anonDiscount) + ' ' + currency;
        }
        if (priceEl) priceEl.classList.remove('myb-fee__price--discounted');
        if (oldEl) oldEl.classList.toggle('d-none', isNaN(anonDiscount));
      });

      // Banner sticky: restaurar el render anónimo del servidor.
      var sticky = document.querySelector('.widget-banner__price');
      if (sticky && sticky.dataset.mybOriginalHtml) {
        sticky.innerHTML = sticky.dataset.mybOriginalHtml;
      }
    },

    _togglePassword: function () {
      var input = document.getElementById('mybarcelo-password');
      var btn   = document.getElementById('mybarcelo-toggle-password');
      if (!input) return;
      var isText = input.type === 'text';
      input.type = isText ? 'password' : 'text';
      if (btn) btn.setAttribute('aria-label', isText ? 'Mostrar contraseña' : 'Ocultar contraseña');
    },
  };

  // ── Boot ──────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    var cfg = window.myBarceloConfig;
    if (!cfg) return;

    var auth  = new MyBarceloAuth(cfg);
    var modal = new MyBarceloModal(auth);
    modal.init();

    // El registro necesita el PKCE y la cola de login de `auth`, y el
    // conmutador de pestañas de `modal`. Sin esto, las instancias se quedan
    // encerradas en este IIFE y mybarcelo-register.js no puede iniciar sesión
    // tras el alta. Mismo patrón que window.BexForgotPassword.
    window.MyBarceloAuth  = auth;
    window.MyBarceloModal = modal;
  });

})();
